import classNames from "classnames";
import { useFeature } from "flagged";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import ReactTooltip from "react-tooltip";

import style from "./manage-role.scss";
import GroupIcon from "components/icons/GroupIcon";
import UserIcon from "components/icons/UserIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { INTERNAL_TENANT_TYPE, TOOLTIP_DELAY } from "domain/globalConstants";
import { AvailableLicenseData } from "domain/licenses";
import {
    Authority,
    EMS_AUTHORITIES,
    RoleInUserResponse,
    ROOT_ONLY_AUTHORITIES,
    TARGET_AUTHORITIES,
    Targets,
} from "domain/roles";
import { FeatureLicenseType, GroupWithRole } from "domain/users";
import {
    FLAG_ASSET_MANAGEMENT,
    FLAG_FILE_SHARING,
    FLAG_REPORT_SIGNATURE_KEYS,
} from "services/feature/FeatureFlagService";
import { hasEmsId } from "services/licenses/licenseRepository";
import { licenseService } from "services/licenses/LicenseService";
import { isCurrentTenantInternal } from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import form from "styles/form.scss";
import { getInitials } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import checkMark from "assets/images/icons/checkMark.svg";
import lockIcon from "assets/images/icons/lockIcon.svg";

import testIds from "testIds.json";

interface Props {
    authorities: string[];
    onView: boolean;
    isManager: boolean;
    setFieldValue?: (
        field: string,
        value: string[] | Targets | undefined,
        shouldValidate?: boolean | undefined
    ) => void;
    resetCounter?: number;
    targets?: Targets;
    useNewLayout?: boolean;
    userGroups?: GroupWithRole[];
    userRole?: RoleInUserResponse;
    selectedTab?: number;
}

export enum TabIndex {
    INTEGRATION_TAB,
    LICENSES_TAB,
    REPORTS_TAB,
    CUSTOM_REPORTS_TAB,
    ROLES_TAB,
    USERS_TAB,
    AUDIT_LOGS_TAB,
    FILE_SHARING_TAB,
    OPTIONAL_TAB,
}

export function deriveImplicitAuthorities(
    bareAuthorities: BareAuthority[],
    implicitAuthority: Authority
): LinkedAuthority[] {
    const allAuthorities = new Set<Authority>();
    const nonImplicitAuthorities: Authority[] = [];
    for (const each of bareAuthorities) {
        if (allAuthorities.has(each.value)) {
            throw new Error("Invalid state: specifications should only contain each authority once.");
        }
        allAuthorities.add(each.value);
        if (each.value !== implicitAuthority) {
            nonImplicitAuthorities.push(each.value);
        }
    }
    return bareAuthorities.map((each) => {
        const implicit = each.value === implicitAuthority;
        return {
            ...each,
            implicitRoleAuthorities: implicit ? [] : [implicitAuthority],
            implicitOfRoleAuthorities: implicit ? nonImplicitAuthorities : [],
        };
    });
}

export interface BareAuthority {
    value: Authority;
    testId: string;
    label: string;
}

export interface LinkedAuthority extends BareAuthority {
    implicitRoleAuthorities: Authority[];
    implicitOfRoleAuthorities: Authority[];
}

function trimArrays<T>(arrays: T[][]): T[][] {
    return arrays.filter((each) => each.length > 0);
}

const AuthorityView = (rawProps: Props): JSX.Element => {
    const props = { resetCounter: 0, ...rawProps };
    const { t } = useTranslation();
    const ON_PREMISE_BMP_LICENSE_TYPE_ID = "51";
    const tenantDetails = useSelector(
        (state: StoreState) => state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1]
    );
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const theme = useTheme();
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const [selectedTarget, setSelectedTarget] = React.useState("NONE");
    const [visibleAuthorities, setVisibleAuthorities] = React.useState("");
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string>("");
    const [authoritiesLoading, setAuthoritiesLoading] = React.useState<boolean>(false);

    const [onPremiseBmpLicenseExists, setOnPremiseBmpLicenseExists] = React.useState<boolean>(false);
    const targets = [
        {
            targetId: "NONE",
            targetName: t("ManageRoleForm.reports.targets.none"),
        },
        { targetId: "TENANT", targetName: t("ManageRoleForm.reports.targets.tenant") },
        { targetId: "GROUP", targetName: t("ManageRoleForm.reports.targets.group") },
        { targetId: "USER", targetName: t("ManageRoleForm.reports.targets.user") },
    ];
    const showWorkflow =
        tenantDetails.featureLicenses.includes("FEATURE_WORKFLOW_BDE") ||
        tenantDetails.featureLicenses.includes("FEATURE_WORKFLOW_BMDE") ||
        tenantDetails.featureLicenses.includes("FEATURE_WORKFLOW_LUN") ||
        tenantDetails.featureLicenses.includes("FEATURE_WORKFLOW_BMP");

    const hasBmpWorkflowLicense = tenantDetails.featureLicenses.includes("FEATURE_WORKFLOW_BMP");
    const hasFileSharingAuthorities = tenantDetails.featureLicenses.includes("FEATURE_FILE_SHARING");
    const fileSharingQueryFlag = useFeature(FLAG_FILE_SHARING);
    const customReportSignatureQueryFlag = useFeature(FLAG_REPORT_SIGNATURE_KEYS);
    const hasAiLicense =
        tenantDetails.featureLicenses.includes("FEATURE_AI_AGENT") ||
        tenantDetails.featureLicenses.includes("FEATURE_MCP");

    const fetchLicenseExists = async (licenseType: string, abortController: AbortController) => {
        licenseService
            .fetchAvailableLicenses(licenseType, abortController.signal)
            .then((data: AvailableLicenseData) => {
                setOnPremiseBmpLicenseExists(data.expiration != null && data.expiration.length > 0);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("ManageRoleForm.failedToFetchAuthorities"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setAuthoritiesLoading(false);
                }
            });
    };

    const authorities = [
        [
            [
                {
                    value: Authority.AUTH_API_KEY_CREATE_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.createCheckbox,
                    label: t("ManageRoleForm.integration.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_API_KEY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_API_KEY_EDIT_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.editCheckbox,
                    label: t("ManageRoleForm.integration.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_API_KEY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_API_KEY_DELETE_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.deleteCheckbox,
                    label: t("ManageRoleForm.integration.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_API_KEY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_API_KEY_VIEW_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.viewOwnCheckbox,
                    label: t("ManageRoleForm.integration.viewOwnLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_API_KEY_CREATE_OWN,
                        Authority.AUTH_API_KEY_EDIT_OWN,
                        Authority.AUTH_API_KEY_DELETE_OWN,
                        Authority.AUTH_API_KEY_VIEW_ALL,
                    ],
                },
            ],
            [
                {
                    value: Authority.AUTH_API_KEY_VIEW_ALL,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.viewAllCheckbox,
                    label: t("ManageRoleForm.integration.viewAllLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_API_KEY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_SSO_CONFIGURE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.configureSsoCheckBox,
                    label: t("ManageRoleForm.integration.configureSso"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [],
                },
            ],
        ],
        [
            [
                {
                    value: Authority.AUTH_KEY_VALUE_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.createCheckbox,
                    label: t("ManageRoleForm.licenses.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_KEY_VALUE_VIEW, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_KEY_VALUE_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.editCheckbox,
                    label: t("ManageRoleForm.licenses.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_KEY_VALUE_VIEW, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_KEY_VALUE_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.deleteCheckbox,
                    label: t("ManageRoleForm.licenses.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_KEY_VALUE_VIEW, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_KEY_VALUE_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewCheckbox,
                    label: t("ManageRoleForm.licenses.viewLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_KEY_VALUE_CREATE,
                        Authority.AUTH_KEY_VALUE_EDIT,
                        Authority.AUTH_KEY_VALUE_DELETE,
                    ],
                },
            ],
            [
                {
                    value: Authority.AUTH_LICENSE_ASSIGN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.moveLicensesCheckbox,
                    label: t("ManageRoleForm.licenses.moveLicensesLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewLicensesCheckbox,
                    label: t("ManageRoleForm.licenses.viewLicensesLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [Authority.AUTH_LICENSE_POOL_VIEW],
                },
                {
                    value: Authority.AUTH_LICENSE_ALERT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.manageLicenseAlertsCheckbox,
                    label: t("ManageRoleForm.licenses.manageLicenseAlertsLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_VIEW, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_VIEW_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewOwnLicensesCheckbox,
                    label: t("ManageRoleForm.licenses.viewOwnLicensesLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [Authority.AUTH_LICENSE_KEY_MANAGEMENT],
                },
                {
                    value: Authority.AUTH_LICENSE_KEY_MANAGEMENT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.licenseKeyManagementCheckbox,
                    label: t("ManageRoleForm.licenses.licenseKeyManagementLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_VIEW_OWN, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_HISTORY_VIEW_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewOwnLicensesHistoryCheckbox,
                    label: t("ManageRoleForm.licenses.viewOwnLicensesHistoryLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_KEY_VALUE_CREATE,
                        Authority.AUTH_KEY_VALUE_EDIT,
                        Authority.AUTH_KEY_VALUE_DELETE,
                        Authority.AUTH_KEY_VALUE_VIEW,
                        Authority.AUTH_LICENSE_ASSIGN,
                        Authority.AUTH_LICENSE_VIEW,
                        Authority.AUTH_LICENSE_HISTORY_VIEW_ALL,
                        Authority.AUTH_LICENSE_CONSUME,
                        Authority.AUTH_EMS_CREATE_ENTITLEMENT,
                        Authority.AUTH_EMS_SYNCHRONIZE,
                        Authority.AUTH_EMS_VIEW,
                        Authority.AUTH_LICENSE_POOL_CREATE,
                        Authority.AUTH_LICENSE_POOL_EDIT,
                        Authority.AUTH_LICENSE_POOL_DELETE,
                        Authority.AUTH_LICENSE_POOL_VIEW,
                        Authority.AUTH_LICENSE_VIEW_OWN,
                        Authority.AUTH_LICENSE_KEY_MANAGEMENT,
                    ],
                },
                {
                    value: Authority.AUTH_LICENSE_HISTORY_VIEW_ALL,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewAllLicensesHistoryCheckbox,
                    label: t("ManageRoleForm.licenses.viewAllLicensesHistoryLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
            ],
            [
                {
                    value: Authority.AUTH_LICENSE_CONSUME,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.consumeLicensesCheckbox,
                    label: t("ManageRoleForm.licenses.consumeLicensesLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_EMS_CREATE_ENTITLEMENT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.createAndEditCheckbox,
                    label: t("ManageRoleForm.licenses.createAndEditLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_EMS_SYNCHRONIZE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.synchroniseCheckbox,
                    label: t("ManageRoleForm.licenses.synchroniseLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_EMS_VIEW, Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_EMS_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.viewEmsCheckbox,
                    label: t("ManageRoleForm.licenses.viewEMS"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN],
                    implicitOfRoleAuthorities: [Authority.AUTH_EMS_SYNCHRONIZE],
                },
            ],
            [
                {
                    value: Authority.AUTH_LICENSE_POOL_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licensePool.createCheckbox,
                    label: t("ManageRoleForm.licensePools.licensePoolCreateLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_LICENSE_HISTORY_VIEW_OWN,
                        Authority.AUTH_LICENSE_VIEW,
                        Authority.AUTH_LICENSE_POOL_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_POOL_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licensePool.editCheckbox,
                    label: t("ManageRoleForm.licensePools.licensePoolEditLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_LICENSE_HISTORY_VIEW_OWN,
                        Authority.AUTH_LICENSE_VIEW,
                        Authority.AUTH_LICENSE_POOL_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_POOL_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licensePool.deleteCheckbox,
                    label: t("ManageRoleForm.licensePools.licensePoolDeleteLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_LICENSE_HISTORY_VIEW_OWN,
                        Authority.AUTH_LICENSE_POOL_VIEW,
                        Authority.AUTH_LICENSE_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_LICENSE_POOL_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.licensePool.viewCheckbox,
                    label: t("ManageRoleForm.licensePools.licensePoolViewLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_LICENSE_HISTORY_VIEW_OWN, Authority.AUTH_LICENSE_VIEW],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_LICENSE_POOL_CREATE,
                        Authority.AUTH_LICENSE_POOL_EDIT,
                        Authority.AUTH_LICENSE_POOL_DELETE,
                    ],
                },
            ],
        ],
        [
            deriveImplicitAuthorities(
                [
                    {
                        value: Authority.AUTH_REPORT_TEMPLATE_CREATE,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.createCheckbox,
                        label: t("ManageRoleForm.reports.createTemplatesLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_TEMPLATE_EDIT,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.editCheckbox,
                        label: t("ManageRoleForm.reports.editTemplatesLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_TEMPLATE_DELETE,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.deleteCheckbox,
                        label: t("ManageRoleForm.reports.deleteTemplatesLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_TEMPLATE_VIEW,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.viewCheckbox,
                        label: t("ManageRoleForm.reports.viewTemplatesLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_TEMPLATE_DEFAULT,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.defaultCheckbox,
                        label: t("ManageRoleForm.reports.defaultTemplatesLabel"),
                    },
                ],
                Authority.AUTH_REPORT_TEMPLATE_VIEW
            ),
            deriveImplicitAuthorities(
                [
                    {
                        value: Authority.AUTH_REPORT_VIEW,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.viewReportsCheckbox,
                        label: t("ManageRoleForm.reports.viewReportsLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_IMPORT,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.importReportsCheckbox,
                        label: t("ManageRoleForm.reports.importReportsLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_EXPORT_ALL,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.exportReportsCheckbox,
                        label: t("ManageRoleForm.reports.exportAllReportsLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_DELETE,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.deleteReportsCheckbox,
                        label: t("ManageRoleForm.reports.deleteReportsLabel"),
                    },
                    {
                        value: Authority.AUTH_REPORT_EDIT,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.editReportsCheckbox,
                        label: t("ManageRoleForm.reports.editReportsLabel"),
                    },
                ],
                Authority.AUTH_REPORT_VIEW
            ),
        ],
        [
            [
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.createCheckbox,
                    label: t("ManageRoleForm.customReports.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.editCheckbox,
                    label: t("ManageRoleForm.customReports.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.deleteCheckbox,
                    label: t("ManageRoleForm.customReports.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.viewCheckbox,
                    label: t("ManageRoleForm.customReports.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN,
                        Authority.AUTH_CUSTOM_REPORT_VIEW_EDIT_OWN,
                        Authority.AUTH_CUSTOM_REPORT_VIEW_DELETE_OWN,
                        Authority.AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED,
                        Authority.AUTH_CUSTOM_REPORT_VIEW_EDIT_SHARED,
                        Authority.AUTH_CUSTOM_REPORT_VIEW_DELETE_SHARED,
                    ],
                },
            ],
            [
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports
                        .createSharedReportsCheckbox,
                    label: t("ManageRoleForm.customReports.importReportsLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_EDIT_SHARED,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.editSharedReportsCheckbox,
                    label: t("ManageRoleForm.customReports.exportReportsLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_REPORT_VIEW_DELETE_SHARED,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports
                        .deleteSharedReportsCheckbox,
                    label: t("ManageRoleForm.customReports.deleteReportsLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_REPORT_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
            ],
            [
                {
                    value: Authority.AUTH_SIGNATURE_KEY_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports
                        .createSignatureKeyCheckbox,
                    label: t("ManageRoleForm.customReports.createSignatureKeyLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_SIGNATURE_KEY_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_SIGNATURE_KEY_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.editSignatureKeyCheckbox,
                    label: t("ManageRoleForm.customReports.editSignatureKeyLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_SIGNATURE_KEY_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_SIGNATURE_KEY_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports
                        .deleteSignatureKeyCheckbox,
                    label: t("ManageRoleForm.customReports.deleteSignatureKeyLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_SIGNATURE_KEY_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_SIGNATURE_KEY_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.viewSignatureKeyCheckbox,
                    label: t("ManageRoleForm.customReports.viewSignatureKeyLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_SIGNATURE_KEY_CREATE,
                        Authority.AUTH_SIGNATURE_KEY_EDIT,
                        Authority.AUTH_SIGNATURE_KEY_DELETE,
                    ],
                },
            ],
        ],
        [
            [
                {
                    value: Authority.AUTH_ROLE_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.createCheckbox,
                    label: t("ManageRoleForm.roles.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ROLE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ROLE_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.editCheckbox,
                    label: t("ManageRoleForm.roles.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ROLE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ROLE_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.deleteCheckbox,
                    label: t("ManageRoleForm.roles.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ROLE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ROLE_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.viewCheckbox,
                    label: t("ManageRoleForm.roles.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_ROLE_CREATE,
                        Authority.AUTH_ROLE_EDIT,
                        Authority.AUTH_ROLE_DELETE,
                        Authority.AUTH_ROLE_DEFAULT,
                    ],
                },
            ],
            [
                {
                    value: Authority.AUTH_ROLE_DEFAULT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.defaultCheckbox,
                    label: t("ManageRoleForm.roles.defaultLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ROLE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
            ],
        ],
        deriveUserTabAuthorities(tenantDetails.featureLicenses, t),
        [
            [
                {
                    value: Authority.AUTH_AUDIT_LOG_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.auditLogs.deleteCheckbox,
                    label: t("ManageRoleForm.auditLogs.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_AUDIT_LOG_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_AUDIT_LOG_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.auditLogs.viewCheckbox,
                    label: t("ManageRoleForm.auditLogs.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [Authority.AUTH_AUDIT_LOG_DELETE],
                },
            ],
        ],
    ];

    if (hasFileSharingAuthorities && fileSharingQueryFlag) {
        authorities.push([
            [
                {
                    value: Authority.AUTH_FILESHARE_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.fileSharing.createCheckbox,
                    label: t("ManageRoleForm.fileSharing.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_FILESHARE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_FILESHARE_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.fileSharing.editCheckbox,
                    label: t("ManageRoleForm.fileSharing.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_FILESHARE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_FILESHARE_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.fileSharing.deleteCheckbox,
                    label: t("ManageRoleForm.fileSharing.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_FILESHARE_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_FILESHARE_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.fileSharing.viewCheckbox,
                    label: t("ManageRoleForm.fileSharing.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_FILESHARE_CREATE,
                        Authority.AUTH_FILESHARE_EDIT,
                        Authority.AUTH_FILESHARE_DELETE,
                    ],
                },
            ],
        ]);
    } else {
        authorities.push([]);
    }

    if (showWorkflow) {
        const workflowAuthorities = [
            [
                {
                    value: Authority.AUTH_WORKFLOW_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.viewCheckbox,
                    label: t("ManageRoleForm.workflows.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_WORKFLOW_CREATE,
                        Authority.AUTH_WORKFLOW_EDIT,
                        Authority.AUTH_WORKFLOW_DELETE,
                        Authority.AUTH_WORKFLOW_DEFAULT,
                        Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
                        Authority.AUTH_WORKFLOW_TEMPLATE_SAVE,
                        Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                        Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                        Authority.AUTH_WORKFLOW_TEMPLATE_DELETE,
                        Authority.AUTH_WORKFLOW_TEMPLATE_PUBLISH,
                        Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                        Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                        Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
                        Authority.AUTH_WORKFLOW_ENABLE,
                        Authority.AUTH_WORKFLOW_SCHEDULE,
                        Authority.AUTH_WORKFLOW_SECURED,
                        Authority.AUTH_WORKFLOW_TEST,
                        Authority.AUTH_WORKFLOW_LAUNCH,
                        Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                    ],
                },
                {
                    value: Authority.AUTH_WORKFLOW_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.createCheckbox,
                    label: t("ManageRoleForm.workflows.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
                    implicitOfRoleAuthorities: [Authority.AUTH_WORKFLOW_TEMPLATE_SAVE],
                },
                {
                    value: Authority.AUTH_WORKFLOW_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.editCheckbox,
                    label: t("ManageRoleForm.workflows.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_WORKFLOW_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.deleteCheckbox,
                    label: t("ManageRoleForm.workflows.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_WORKFLOW_DEFAULT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.defaultCheckbox,
                    label: t("ManageRoleForm.workflows.defaultLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
            ],
        ];
        if (hasBmpWorkflowLicense) {
            const bmpWorkflowAuthorities = [
                {
                    value: Authority.AUTH_WORKFLOW_ENABLE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.enableCheckbox,
                    label: t("ManageRoleForm.workflows.enableLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_WORKFLOW_CREATE,
                        Authority.AUTH_WORKFLOW_TEST,
                        Authority.AUTH_WORKFLOW_SCHEDULE,
                        Authority.AUTH_WORKFLOW_SECURED,
                        Authority.AUTH_WORKFLOW_LAUNCH,
                        Authority.AUTH_WORKFLOW_EDIT,
                        Authority.AUTH_WORKFLOW_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_WORKFLOW_TEST,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.testCheckbox,
                    label: t("ManageRoleForm.workflows.testLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_WORKFLOW_CREATE,
                        Authority.AUTH_WORKFLOW_EDIT,
                        Authority.AUTH_WORKFLOW_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_WORKFLOW_SCHEDULE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.scheduleCheckbox,
                    label: t("ManageRoleForm.workflows.scheduleLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_WORKFLOW_CREATE,
                        Authority.AUTH_WORKFLOW_EDIT,
                        Authority.AUTH_WORKFLOW_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                },
            ];
            if (isCurrentTenantInternal()) {
                bmpWorkflowAuthorities.push({
                    value: Authority.AUTH_WORKFLOW_SECURED,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.securedCheckbox,
                    label: t("ManageRoleForm.workflows.securedLabel"),
                    implicitRoleAuthorities: [
                        Authority.AUTH_WORKFLOW_CREATE,
                        Authority.AUTH_WORKFLOW_EDIT,
                        Authority.AUTH_WORKFLOW_VIEW,
                    ],
                    implicitOfRoleAuthorities: [],
                });
            }

            bmpWorkflowAuthorities.push({
                value: Authority.AUTH_WORKFLOW_LAUNCH,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.manualLaunchCheckbox,
                label: t("ManageRoleForm.workflows.manualLaunchLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_CREATE,
                    Authority.AUTH_WORKFLOW_EDIT,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            });

            workflowAuthorities.push(bmpWorkflowAuthorities);
        }
        workflowAuthorities.push(deriveWorkflowTemplateManagementAuthorities(t, hasBmpWorkflowLicense));
        workflowAuthorities.push(deriveSaveWorkflowTemplateAuthorities(t));
        authorities.push(workflowAuthorities);
    } else {
        authorities.push([]);
    }
    if (onPremiseBmpLicenseExists) {
        authorities.push([
            deriveImplicitAuthorities(
                [
                    {
                        value: Authority.AUTH_MC_CREATE,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.mcs.createCheckbox,
                        label: t("ManageRoleForm.mcs.createLabel"),
                    },
                    {
                        value: Authority.AUTH_MC_VIEW,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.mcs.viewCheckbox,
                        label: t("ManageRoleForm.mcs.viewLabel"),
                    },
                    {
                        value: Authority.AUTH_MC_EDIT,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.mcs.editCheckbox,
                        label: t("ManageRoleForm.mcs.editLabel"),
                    },
                    {
                        value: Authority.AUTH_MC_UNREGISTER,
                        testId: testIds.workArea.roles.manageRoleDialog.authorities.mcs.unregisterCheckbox,
                        label: t("ManageRoleForm.mcs.unregisterLabel"),
                    },
                ],
                Authority.AUTH_MC_VIEW
            ),
        ]);
    } else {
        authorities.push([]);
    }
    if (tenantDetails.featureLicenses.includes("FEATURE_TENANT_MANAGEMENT")) {
        authorities.push([
            [
                {
                    value: Authority.AUTH_TENANT_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.createCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_TENANT_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_TENANT_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.editCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_TENANT_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_TENANT_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.deleteCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_TENANT_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_TENANT_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.viewCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_TENANT_CREATE,
                        Authority.AUTH_TENANT_EDIT,
                        Authority.AUTH_TENANT_DELETE,
                        Authority.AUTH_TENANT_ACCESS,
                    ],
                },
            ],
            [
                {
                    value: Authority.AUTH_TENANT_ACCESS,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.accessTenantCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.accessLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_TENANT_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_TENANT_SETTINGS,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.tenantSettingsCheckbox,
                    label: t("ManageRoleForm.tenantsAccess.tenantSettingLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [],
                },
            ],
        ]);
    } else {
        authorities.push([]);
    }
    if (tenantDetails.featureLicenses.includes("FEATURE_ASSET_MANAGEMENT") && useFeature(FLAG_ASSET_MANAGEMENT)) {
        authorities.push([
            [
                {
                    value: Authority.AUTH_ASSET_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.createCheckbox,
                    label: t("ManageRoleForm.assetManagement.createLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ASSET_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.editCheckbox,
                    label: t("ManageRoleForm.assetManagement.editLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ASSET_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.deleteCheckbox,
                    label: t("ManageRoleForm.assetManagement.deleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ASSET_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.viewCheckbox,
                    label: t("ManageRoleForm.assetManagement.viewLabel"),
                    implicitRoleAuthorities: [],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_ASSET_CREATE,
                        Authority.AUTH_ASSET_EDIT,
                        Authority.AUTH_ASSET_DELETE,
                        Authority.AUTH_ASSET_GRADING,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_CREATE,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_EDIT,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_DELETE,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW,
                        Authority.AUTH_ASSET_MANIFEST_EDIT,
                        Authority.AUTH_ASSET_MANIFEST_VIEW,
                    ],
                },
                {
                    value: Authority.AUTH_ASSET_GRADING,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.gradeCheckbox,
                    label: t("ManageRoleForm.assetManagement.gradeLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW, Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW],
                    implicitOfRoleAuthorities: [],
                },
            ],
            [
                {
                    value: Authority.AUTH_CUSTOM_ASSET_VIEW_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement
                        .customViewCreateCheckbox,
                    label: t("ManageRoleForm.assetManagement.customViewCreateLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW, Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_ASSET_VIEW_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.customViewEditCheckbox,
                    label: t("ManageRoleForm.assetManagement.customViewEditLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW, Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_ASSET_VIEW_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement
                        .customViewDeleteCheckbox,
                    label: t("ManageRoleForm.assetManagement.customViewDeleteLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW, Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_CUSTOM_ASSET_VIEW_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.customViewViewCheckbox,
                    label: t("ManageRoleForm.assetManagement.customViewViewLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [
                        Authority.AUTH_CUSTOM_ASSET_VIEW_CREATE,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_EDIT,
                        Authority.AUTH_CUSTOM_ASSET_VIEW_DELETE,
                        Authority.AUTH_ASSET_GRADING,
                    ],
                },
                {
                    value: Authority.AUTH_ASSET_MANIFEST_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.manifestEditCheckbox,
                    label: t("ManageRoleForm.assetManagement.manifestEditLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
                {
                    value: Authority.AUTH_ASSET_MANIFEST_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.manifestViewCheckbox,
                    label: t("ManageRoleForm.assetManagement.manifestViewLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [Authority.AUTH_ASSET_MANIFEST_EDIT],
                },
            ],
            [
                {
                    value: Authority.AUTH_ASSET_INTEGRATION,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement
                        .integrateWithThirdPartyCheckbox,
                    label: t("ManageRoleForm.assetManagement.assetIntegrationLabel"),
                    implicitRoleAuthorities: [Authority.AUTH_ASSET_CREATE, Authority.AUTH_ASSET_VIEW],
                    implicitOfRoleAuthorities: [],
                },
            ],
        ]);
    } else {
        authorities.push([]);
    }
    authorities.push([
        [
            {
                value: Authority.AUTH_AI_ASSISTANT,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.ai.assistantCheckbox,
                label: t("ManageRoleForm.ai.assistantLabel"),
                implicitRoleAuthorities: [],
                implicitOfRoleAuthorities: [],
            },
            {
                value: Authority.AUTH_AI_MCP,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.ai.mcpCheckbox,
                label: t("ManageRoleForm.ai.mcpLabel"),
                implicitRoleAuthorities: [],
                implicitOfRoleAuthorities: [],
            },
        ],
    ]);

    const tabData = {
        authorities: authorities,
    };
    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>, implicitRoleAuthorities: string[]) => {
        const authorities = [...props.authorities];
        if (event.target.checked) {
            authorities.push(event.target.value);
            implicitRoleAuthorities &&
                implicitRoleAuthorities.map((authority) => {
                    const checkbox = document.getElementById(authority) as HTMLInputElement | null;
                    if (checkbox != null) {
                        checkbox.checked = true;
                        checkbox.disabled = true;
                        if (!authorities.includes(authority)) {
                            authorities.push(authority);
                        }
                    }
                });
        } else if (!event.target.checked) {
            const index = authorities.indexOf(event.target.value);
            authorities.splice(index, 1);
            implicitRoleAuthorities &&
                implicitRoleAuthorities.map((authority: Authority) => {
                    tabData.authorities.map((item) => {
                        item.map((each) => {
                            const index = each.map((e) => e.value).indexOf(authority);
                            if (index >= 0) {
                                const implicitOfRoleAuthorities: Authority[] = each[index].implicitOfRoleAuthorities;
                                if (
                                    implicitOfRoleAuthorities &&
                                    !authorities.some((element: Authority) =>
                                        implicitOfRoleAuthorities.includes(element)
                                    )
                                ) {
                                    const checkbox = document.getElementById(authority) as HTMLInputElement | null;
                                    if (checkbox != null) {
                                        checkbox.checked = false;
                                        checkbox.disabled = false;
                                        const index = authorities.indexOf(authority);
                                        authorities.splice(index, 1);
                                    }
                                }
                            }
                        });
                    });
                });
        }
        props.setFieldValue && props.setFieldValue("authorities", authorities);
        setAuthorityTargets(authorities);
    };
    const [selectedTab, setSelectedTab] = React.useState(props.selectedTab ?? TabIndex.INTEGRATION_TAB);
    const handleTabSelect = (index: number, lastIndex: number) => {
        if (index !== lastIndex) {
            setSelectedTab(index as TabIndex);
        }
        return true;
    };

    const handleTargetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedTarget(event.target.value);
    };

    function setAuthorityTargets(propsAuthorities: string[]) {
        if (selectedTarget != "NONE" && TARGET_AUTHORITIES.some((authority) => propsAuthorities.includes(authority))) {
            const targets: Targets = {
                authorities: {},
            };
            TARGET_AUTHORITIES.forEach((authority) => {
                if (propsAuthorities.includes(authority)) {
                    targets.authorities[authority] = {
                        targets: [
                            {
                                kind: "relative",
                                value: selectedTarget,
                            },
                        ],
                    };
                }
            });
            props.setFieldValue && props.setFieldValue("targets", targets);
        } else {
            props.setFieldValue && props.setFieldValue("targets", undefined);
        }
    }

    const isImplicitAuthoritySelected = (implicitAuthority: string, implicitOfRoleAuthorities: string[]) => {
        let found = false;
        for (let i = 0; i < implicitOfRoleAuthorities.length; i++) {
            if (props.authorities.includes(implicitOfRoleAuthorities[i])) {
                !props.authorities.includes(implicitAuthority) && props.authorities.push(implicitAuthority);
                found = true;
                break;
            }
        }
        return found;
    };

    const conditionallyVisibleAuthorities: Partial<Record<Authority, boolean>> = {
        [Authority.AUTH_SSO_CONFIGURE]: tenantDetails.featureLicenses.includes("FEATURE_SSO"),
        [Authority.AUTH_AI_ASSISTANT]: tenantDetails.featureLicenses.includes("FEATURE_AI_AGENT"),
        [Authority.AUTH_AI_MCP]: tenantDetails.featureLicenses.includes("FEATURE_MCP"),
        [Authority.AUTH_SIGNATURE_KEY_CREATE]: Boolean(customReportSignatureQueryFlag),
        [Authority.AUTH_SIGNATURE_KEY_EDIT]: Boolean(customReportSignatureQueryFlag),
        [Authority.AUTH_SIGNATURE_KEY_DELETE]: Boolean(customReportSignatureQueryFlag),
        [Authority.AUTH_SIGNATURE_KEY_VIEW]: Boolean(customReportSignatureQueryFlag),
    };

    function createCheckbox(
        checkboxValue: Authority,
        dataTestId: string,
        label: string,
        implicitRoleAuthorities: string[],
        implicitOfRoleAuthorities: string[]
    ) {
        if (conditionallyVisibleAuthorities[checkboxValue] === false) {
            return "";
        }
        if (EMS_AUTHORITIES.has(checkboxValue) && user?.tenantType != INTERNAL_TENANT_TYPE && !hasEmsId()) {
            return "";
        }
        if (user?.tenantType != INTERNAL_TENANT_TYPE && ROOT_ONLY_AUTHORITIES.has(checkboxValue)) {
            return "";
        }
        return (
            <div key={dataTestId} className={style.rolesCheckbox}>
                {props.onView ? (
                    <img
                        key="selected-data"
                        src={props.authorities.includes(checkboxValue) ? checkMark : lockIcon}
                        className={style.viewCheckIcon}
                    />
                ) : (
                    <label className={form.container}>
                        <input
                            key={checkboxValue}
                            name="authorities"
                            id={checkboxValue}
                            type="checkbox"
                            defaultChecked={props.authorities.includes(checkboxValue)}
                            data-testid={dataTestId}
                            className={form.input}
                            value={checkboxValue}
                            onChange={(e) => handleOnChange(e, implicitRoleAuthorities)}
                            disabled={
                                props.isManager || isImplicitAuthoritySelected(checkboxValue, implicitOfRoleAuthorities)
                            }
                        />
                        <span className={form.checkmark} />
                    </label>
                )}
                <label
                    htmlFor={checkboxValue}
                    className={classNames(form.label, props.isManager ? style.disabledText : style.labelMargin)}
                >
                    {label}
                </label>
            </div>
        );
    }

    function extractAuthorityTarget() {
        if (!props.targets) {
            return;
        }
        const found = Object.values(props.targets.authorities)
            .map((e) => e.targets)
            .flat()
            .filter((e) => e.kind === "relative")
            .map((e) => e.value)
            .find((e) => e);
        if (found == null) {
            return;
        }
        setSelectedTarget(found);
    }

    const authorityContentHeaders = [
        {
            name: t("ManageRoleForm.integration.integrationTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.integration.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.licenses.licensesTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.licenses.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.reports.reportsTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.reports.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.customReports.customReportsTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.customReports.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.roles.rolesTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.roles.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.users.usersTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.users.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.auditLogs.auditLogsTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.auditLogs.tab,
            show: true,
        },
        {
            name: t("ManageRoleForm.fileSharing.fileSharingTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.fileSharing.tab,
            show: hasFileSharingAuthorities && fileSharingQueryFlag,
        },
        {
            name: t("ManageRoleForm.workflows.workflowsTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.tab,
            show: showWorkflow,
        },
        {
            name: t("ManageRoleForm.mcs.mcsTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.mcs.tab,
            show: onPremiseBmpLicenseExists,
        },
        {
            name: t("ManageRoleForm.tenantsAccess.tenantAccessTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.tenantAccess.tab,
            show: tenantDetails.featureLicenses.includes("FEATURE_TENANT_MANAGEMENT"),
        },
        {
            name: t("ManageRoleForm.assetManagement.assetManagementTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.assetManagement.tab,
            show:
                tenantDetails.featureLicenses.includes("FEATURE_ASSET_MANAGEMENT") && useFeature(FLAG_ASSET_MANAGEMENT),
        },
        {
            name: t("ManageRoleForm.ai.aiTab"),
            testId: testIds.workArea.roles.manageRoleDialog.authorities.ai.tab,
            show: hasAiLicense,
        },
    ];

    const createChip = (authority: LinkedAuthority) => {
        if (props.userGroups == null && props.userRole == null) {
            return;
        }

        const userGroups = props.userGroups?.filter((source) => source.role?.authorities.includes(authority.value));
        const includedInUserRole = props.userRole?.authorities.includes(authority.value);
        const disabled =
            (visibleAuthorities === "user" && !includedInUserRole) ||
            (visibleAuthorities === "group" && userGroups && userGroups.length < 1);

        const showGroupInitials = () => {
            const maximumGroupIconsCount = 2;
            return (
                <>
                    <div
                        className={style.chipGroupIcons}
                        data-tip
                        data-for={authority.label}
                        data-delay-show={TOOLTIP_DELAY - 400}
                        tabIndex={disabled ? -1 : 0}
                    >
                        {includedInUserRole && <UserIcon color={theme.chipUserIconColor} />}
                        {userGroups && (
                            <>
                                {userGroups
                                    .filter((group) => group.name != null)
                                    .slice(0, maximumGroupIconsCount)
                                    .map((group) => {
                                        return (
                                            <div key={group.name}>
                                                <GroupIcon
                                                    backgroundColor={group.backgroundColor}
                                                    textColor={group.textColor}
                                                    groupInitials={getInitials(group.name)}
                                                    size={20}
                                                />
                                            </div>
                                        );
                                    })}
                                {userGroups.length > maximumGroupIconsCount && (
                                    <div className={style.remainingGroups}>
                                        {t("UsersTable.remainingGroupsCount", {
                                            remainingGroupsCount: userGroups.length - maximumGroupIconsCount,
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <ReactTooltip id={authority.label} className={style.tooltip} type="dark">
                        <ul className={style.groupIconsTooltip}>
                            {includedInUserRole && (
                                <li>
                                    <div className={style.icon}>
                                        <UserIcon color={theme.chipUserIconColor} />
                                    </div>
                                    <span>{props.userRole?.name}</span>
                                </li>
                            )}
                            {userGroups && (
                                <>
                                    {userGroups
                                        .filter((group) => group.name != null)
                                        .map((group) => {
                                            return (
                                                <li key={group.name}>
                                                    <div className={style.icon}>
                                                        <GroupIcon
                                                            backgroundColor={group.backgroundColor}
                                                            textColor={group.textColor}
                                                            groupInitials={getInitials(group.name)}
                                                            size={20}
                                                        />
                                                    </div>
                                                    <span>{group.name}</span>
                                                </li>
                                            );
                                        })}
                                </>
                            )}
                        </ul>
                    </ReactTooltip>
                </>
            );
        };

        return (
            <>
                <div key={authority.label} className={classNames(style.chip, { [style.disabled]: disabled })}>
                    {showGroupInitials()}
                    <span>{authority.label}</span>
                </div>
            </>
        );
    };

    React.useEffect(() => {
        extractAuthorityTarget();
    }, [props.resetCounter]);

    React.useEffect(() => {
        extractAuthorityTarget();
    }, []);

    React.useEffect(() => {
        setAuthorityTargets([...props.authorities]);
    }, [selectedTarget]);

    React.useEffect(() => {
        setAuthoritiesLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        fetchLicenseExists(ON_PREMISE_BMP_LICENSE_TYPE_ID, abortController);

        return () => {
            abortController.abort();
        };
    }, []);

    if (authoritiesLoading) {
        return <LoadingIndicator />;
    } else if (requestFailureMessage.length > 0) {
        return (
            <div className={style.authorityErrorMessage}>
                <div>{t(requestFailureMessage)}</div>
            </div>
        );
    }
    return (
        <div className={style.authorityWrapper} key={props.resetCounter}>
            {props.useNewLayout ? (
                <>
                    <div className={style.authorityFilter}>
                        <div className={form.formFields}>
                            <label htmlFor="role" className={form.label}>
                                {t("ViewUser.authorityFilterLabel")}
                            </label>
                            <select
                                id={"toggleVisibleAuthorities"}
                                value={visibleAuthorities ? visibleAuthorities : "all"}
                                data-testid={testIds.workArea.user.viewUserDialog.authoritySourceSelect}
                                onChange={(event) => setVisibleAuthorities(event.target.value)}
                                className={classNames(form.select, form.fixedWidthInput)}
                            >
                                <option key={"optionAll"} value={"all"}>
                                    {t("ViewUser.authorityFilterAll")}
                                </option>
                                <option key={"optionUser"} value={"user"}>
                                    {t("ViewUser.authorityFilterUsers")}
                                </option>
                                <option key={"optionGroup"} value={"group"}>
                                    {t("ViewUser.authorityFilterGroups")}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div className={style.authorityContainer}>
                        {authorityContentHeaders.map((category, originalIndex) => {
                            if (!category.show) {
                                return null;
                            }
                            return (
                                <div key={category.name} className={style.authorityContent}>
                                    <span>{category.name}</span>
                                    <div className={style.authorityContentRow}>
                                        {(() => {
                                            const authCategory = tabData.authorities[originalIndex];
                                            if (!authCategory) {
                                                return t("Common.none");
                                            }
                                            if (
                                                authCategory
                                                    .map((authorities) =>
                                                        authorities.filter((authority) =>
                                                            props.authorities.includes(authority.value)
                                                        )
                                                    )
                                                    .some((arr) => arr.length > 0)
                                            ) {
                                                return authCategory.map((authorities) =>
                                                    authorities
                                                        .filter((authority) =>
                                                            props.authorities.includes(authority.value)
                                                        )
                                                        .map((authority) => createChip(authority))
                                                );
                                            }
                                            return t("Common.none");
                                        })()}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <>
                    <Tabs selectedIndex={selectedTab} onSelect={handleTabSelect}>
                        <TabList>
                            {authorityContentHeaders.map((item) => {
                                if (!item.show) {
                                    return;
                                }
                                return (
                                    <Tab key={"tab" + item.name} data-testid={item.testId}>
                                        {item.name}
                                    </Tab>
                                );
                            })}
                        </TabList>

                        {tabData.authorities.map((item, i) =>
                            authorityContentHeaders[i]?.show ? (
                                <TabPanel key={i}>
                                    {!props.isManager && i === TabIndex.REPORTS_TAB && (
                                        <div>
                                            {props.onView ? (
                                                <div>
                                                    {t("ManageRoleForm.reports.targets.authorityTarget", {
                                                        target: targets.filter(
                                                            (target) => target.targetId === selectedTarget
                                                        )[0].targetName,
                                                    })}
                                                </div>
                                            ) : (
                                                <div className={style.reportTarget}>
                                                    <div>
                                                        <label htmlFor="reportTarget">
                                                            {t("ManageRoleForm.reports.targets.selectTargetsLabel")}
                                                        </label>
                                                    </div>
                                                    <div>
                                                        <select
                                                            id="reportTarget"
                                                            key={i}
                                                            data-testid={
                                                                testIds.workArea.roles.manageRoleDialog.authorities
                                                                    .reports.targetSelect
                                                            }
                                                            value={selectedTarget}
                                                            onChange={handleTargetChange}
                                                            className={classNames(form.select, form.fixedWidthInput)}
                                                        >
                                                            {targets.map((target) => (
                                                                <option key={target.targetId} value={target.targetId}>
                                                                    {target.targetName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className={style.tabContainer}>
                                        {item.map((each, index) => (
                                            <div key={index} className={style.tabPanel}>
                                                {each.map((valueOfEach) =>
                                                    createCheckbox(
                                                        valueOfEach.value,
                                                        valueOfEach.testId,
                                                        valueOfEach.label,
                                                        [...valueOfEach.implicitRoleAuthorities],
                                                        [...valueOfEach.implicitOfRoleAuthorities]
                                                    )
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </TabPanel>
                            ) : null
                        )}
                    </Tabs>
                </>
            )}
        </div>
    );
};

export default AuthorityView;

function deriveUserTabAuthorities(featureLicenses: FeatureLicenseType[], t: TFunction): LinkedAuthority[][] {
    return trimArrays([
        deriveImplicitAuthorities(
            [
                {
                    value: Authority.AUTH_USER_CREATE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.users.createCheckbox,
                    label: t("ManageRoleForm.users.createLabel"),
                },
                {
                    value: Authority.AUTH_USER_EDIT,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.users.editCheckbox,
                    label: t("ManageRoleForm.users.editLabel"),
                },
                {
                    value: Authority.AUTH_USER_DELETE,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.users.deleteCheckbox,
                    label: t("ManageRoleForm.users.deleteLabel"),
                },
                {
                    value: Authority.AUTH_USER_VIEW,
                    testId: testIds.workArea.roles.manageRoleDialog.authorities.users.viewCheckbox,
                    label: t("ManageRoleForm.users.viewLabel"),
                },
            ],
            Authority.AUTH_USER_VIEW
        ),
        featureLicenses.includes("FEATURE_USER_GROUPS")
            ? deriveImplicitAuthorities(
                  [
                      {
                          value: Authority.AUTH_USER_GROUP_CREATE,
                          testId: testIds.workArea.roles.manageRoleDialog.authorities.users.createGroupsCheckbox,
                          label: t("ManageRoleForm.users.createGroupsLabel"),
                      },
                      {
                          value: Authority.AUTH_USER_GROUP_EDIT,
                          testId: testIds.workArea.roles.manageRoleDialog.authorities.users.editGroupsCheckbox,
                          label: t("ManageRoleForm.users.editGroupsLabel"),
                      },
                      {
                          value: Authority.AUTH_USER_GROUP_DELETE,
                          testId: testIds.workArea.roles.manageRoleDialog.authorities.users.deleteGroupsCheckbox,
                          label: t("ManageRoleForm.users.deleteGroupsLabel"),
                      },
                      {
                          value: Authority.AUTH_USER_GROUP_VIEW,
                          testId: testIds.workArea.roles.manageRoleDialog.authorities.users.viewGroupsCheckbox,
                          label: t("ManageRoleForm.users.viewGroupsLabel"),
                      },
                  ],
                  Authority.AUTH_USER_GROUP_VIEW
              )
            : [],
    ]);
}

function deriveSaveWorkflowTemplateAuthorities(t: TFunction): LinkedAuthority[] {
    return [
        {
            value: Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
            testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.viewTemplateCheckbox,
            label: t("ManageRoleForm.workflows.viewTemplateLabel"),
            implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
            implicitOfRoleAuthorities: [
                Authority.AUTH_WORKFLOW_TEMPLATE_SAVE,
                Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
            ],
        },
        {
            value: Authority.AUTH_WORKFLOW_TEMPLATE_SAVE,
            testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.saveTemplateCheckbox,
            label: t("ManageRoleForm.workflows.saveTemplateLabel"),
            implicitRoleAuthorities: [
                Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
                Authority.AUTH_WORKFLOW_CREATE,
                Authority.AUTH_WORKFLOW_VIEW,
            ],
            implicitOfRoleAuthorities: [],
        },
    ];
}

function deriveWorkflowTemplateManagementAuthorities(t: TFunction, hasBmpWorkflowLicense: boolean): LinkedAuthority[] {
    const result = [];
    if (isCurrentTenantInternal()) {
        result.push(
            {
                value: Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.manageTemplateCheckbox,
                label: t("ManageRoleForm.workflows.manageTemplateLabel"),
                implicitRoleAuthorities: [Authority.AUTH_WORKFLOW_VIEW],
                implicitOfRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                    Authority.AUTH_WORKFLOW_TEMPLATE_DELETE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_PUBLISH,
                    Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
                ],
            },
            {
                value: Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.createTemplateCheckbox,
                label: t("ManageRoleForm.workflows.createTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
                ],
            },
            {
                value: Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.editTemplateCheckbox,
                label: t("ManageRoleForm.workflows.editTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
                ],
            },
            {
                value: Authority.AUTH_WORKFLOW_TEMPLATE_DELETE,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.deleteTemplateCheckbox,
                label: t("ManageRoleForm.workflows.deleteTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            },
            {
                value: Authority.AUTH_WORKFLOW_TEMPLATE_PUBLISH,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.publishTemplateCheckbox,
                label: t("ManageRoleForm.workflows.publishTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            }
        );
        if (hasBmpWorkflowLicense) {
            result.push({
                value: Authority.AUTH_WORKFLOW_TEMPLATE_TEST,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.testTemplateCheckbox,
                label: t("ManageRoleForm.workflows.testTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                    Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            });
            result.push({
                value: Authority.AUTH_WORKFLOW_TEMPLATE_SCHEDULE,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.scheduleTemplateCheckbox,
                label: t("ManageRoleForm.workflows.scheduleTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                    Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            });
            result.push({
                value: Authority.AUTH_WORKFLOW_TEMPLATE_SECURED,
                testId: testIds.workArea.roles.manageRoleDialog.authorities.workflows.securedTemplateCheckbox,
                label: t("ManageRoleForm.workflows.securedTemplateLabel"),
                implicitRoleAuthorities: [
                    Authority.AUTH_WORKFLOW_TEMPLATE_CREATE,
                    Authority.AUTH_WORKFLOW_TEMPLATE_EDIT,
                    Authority.AUTH_WORKFLOW_TEMPLATE_VIEW,
                    Authority.AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW,
                    Authority.AUTH_WORKFLOW_VIEW,
                ],
                implicitOfRoleAuthorities: [],
            });
        }
    }

    return result;
}
